/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Collapsible multiple choice question card with lettered options and correct answer highlight.
 *
 * @file QuestionPreviewCard.jsx
 * @author Anuja Gaikwad
 * @since 3.0.0
 */

import React, { useId, useState } from 'react';
import PropTypes from 'prop-types';
import Icon from '../icons/Icon';
import MathTextComponent from '../common/MathTextComponent';
import { QuestionPreviewCardStylesRoot, classes } from './styles/QuestionPreviewStyles';
import { getOptionLetter } from './questionPreviewUtils';

/**
 * Read-only MCQ card. The header (chevron + stem) toggles the option list; the correct
 * option is highlighted green with a "Correct answer" tag. Reusable anywhere a question
 * needs previewing (preview page, Questions & Topics modal).
 *
 * @param {string} stem question text (may contain MathML)
 * @param {Array<{ id, label, correct }>} options answer choices, in display order
 * @param {boolean} defaultExpanded whether options are visible on first render
 * @param {string} correctAnswerLabel localized tag text for the correct option
 * @param {Function} onMCQExpand called with isExpanded when the user expands or collapses the card
 */
const QuestionPreviewCard = ({
  stem,
  options,
  defaultExpanded,
  correctAnswerLabel,
  onMCQExpand
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const bodyId = useId();

  return (
    <QuestionPreviewCardStylesRoot className={classes.questionCard}>
      <div className={classes.questionCardInner}>
        <button
          type="button"
          className={classes.questionCardHeader}
          onClick={() => {
            setIsExpanded((prev) => !prev);
            onMCQExpand(!isExpanded);
          }}
          aria-expanded={isExpanded}
          aria-controls={bodyId}
        >
          <span
            className={`${classes.questionCardToggleIcon} ${isExpanded ? classes.expandChevronExpanded : classes.expandChevronCollapsed}`}
            aria-hidden="true"
          >
            <Icon name="ExpandIcon" />
          </span>
          <MathTextComponent text={stem} className={classes.questionCardStem} />
        </button>

        {isExpanded && options.length > 0 && (
          <ul id={bodyId} className={classes.optionList}>
            {options.map((option, index) => {
              const isCorrect = Boolean(option.correct);

              return (
                <li
                  key={option.id ?? index}
                  className={`${classes.optionRow} ${isCorrect ? classes.optionRowCorrect : ''}`}
                >
                  <span className={`${classes.optionPrefix} ${isCorrect ? classes.optionPrefixCorrect : ''}`}>
                    {getOptionLetter(index)}
                  </span>
                  <MathTextComponent
                    text={option.label || ''}
                    className={`${classes.optionLabel} ${isCorrect ? classes.optionLabelCorrect : ''}`}
                  />
                  {isCorrect && (
                    <>
                      <span className={classes.correctTag}>{correctAnswerLabel}</span>
                      <span className={classes.correctIcon} aria-hidden="true">
                        <Icon name="PositiveIcon" style={{ width: 18, height: 18 }} />
                      </span>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </QuestionPreviewCardStylesRoot>
  );
};

QuestionPreviewCard.propTypes = {
  stem: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
    correct: PropTypes.bool
  })),
  defaultExpanded: PropTypes.bool,
  correctAnswerLabel: PropTypes.string,
  onMCQExpand: PropTypes.func
};

QuestionPreviewCard.defaultProps = {
  options: [],
  defaultExpanded: false,
  correctAnswerLabel: '',
  onMCQExpand: () => {}
};

export default QuestionPreviewCard;
